<script lang="ts" setup>
import { computed, onMounted, ref, watch, toRefs } from 'vue';
import { getI18nInstance as i18n } from '@/i18n';
import { Event } from '@/models/Event';
import { Schedule, ScheduleCollection } from '@/models/Schedule.js';
import { errToStr, debounce, formRules } from '@/helpers/tools';
import { commitToast } from '@/plugins/toast';
import ScheduleEditDialog from '@/components/schedule/EditDialog.vue';
import DateTextfield from '@/components/form/DateTextfield.vue';
import BaseList from '@/components/BaseList.vue';
import TagSelect from '@/components/tag/select.vue';

const props = defineProps<{
    event: Event;
}>();
const { event } = toRefs(props);

const emit = defineEmits(['saved']);

const headers = [
    { align: 'center', sortable: false, width: 40, value: 'icon' },
    { text: i18n().t('date'), sortable: true, width: 110, value: 'start_date' },
    { text: i18n().t('time'), sortable: true, width: 50, value: 'event_time_start' },
    { text: i18n().t('opening_time_start'), sortable: true, width: 85, value: 'opening_time_start' },
    { text: i18n().t('zusatzinformationen'), sortable: true, value: 'infos' },
    { sortable: false, align: 'center', width: 95, value: 'actions' },
];

const createNewSchedule = (data: object = {}) => {
    return new Schedule({
        event_id: event.value.getId(),
        recurring_type: Schedule.RECURRING_TYPE_SINGLE,
        recur_every_n: null,
        ...data,
    });
};

const showEdit = ref(false);
const schedule = ref(createNewSchedule());
const valid = ref(true);
const loading = ref(false);
const collection = new ScheduleCollection().setRemoteSort({
    property: 'start_date',
    direction: 'asc',
});

const filters = computed(() => {
    return [
        {
            property: 'event_id',
            value: event.value.id || -1,
        },
    ];
});

watch(
    () => event?.value?.id,
    async (newId) => {
        schedule?.value?.set('event_id', newId);
    },
);

const editRecord = (record: Schedule, recurring: boolean | null = null) => {
    if (record.isPhantom() && recurring === null) {
        recurring = false;
    }
    if (recurring === true) {
        record.set('recurring_type', Schedule.RECURRING_TYPE_DAILY);
        record.set('recur_every_n', 1);
    } else if (recurring === false) {
        record.set('recurring_type', Schedule.RECURRING_TYPE_SINGLE);
        record.set('recur_every_n', null);
    }
    schedule.value = record;
    showEdit.value = true;
};

const deleteRecord = async (record: Schedule) => {
    if (record) {
        try {
            loading.value = true;
            await record?.delete();
            emit('saved');
            await reload();
        } catch (e) {
            commitToast({ message: errToStr(e), color: 'error' });
            record?.reset();
        } finally {
            loading.value = false;
        }
    }
};

const reload = async () => {
    loading.value = true;
    try {
        const params = { filter: JSON.stringify(filters.value) };
        await collection.query(params);
    } catch (e) {
        commitToast({ message: errToStr(e), color: 'error' });
    } finally {
        loading.value = false;
    }
    return collection;
};

const saveSchedule = async (record: Schedule, isEdit = true) => {
    if (!record) {
        return;
    }

    // @see 29118
    if (!isEdit && record.start_date < record.end_date) {
        record.recurring_type = Schedule.RECURRING_TYPE_DAILY;
        record.recur_every_n = 1;
    }

    try {
        await record?.save();
        emit('saved', record);
        schedule.value = createNewSchedule();
        await reload();
    } catch (e) {
        commitToast({ message: errToStr(e), color: 'error' });
    }
};

const resetSchedule = (record: Schedule) => {
    record.reset();
    schedule.value = createNewSchedule();
};

const onInput = (field: string, value: any) => {
    schedule.value.set(field, value);
};

onMounted(async () => {
    await reload();
});
</script>

<template>
    <div>
        <h3 class="section-heading">{{ $t('schedule') }}</h3>
        <v-form ref="form" v-model="valid">
            <v-container class="ma-0 py-1 px-0">
                <v-row dense>
                    <v-col cols="6">
                        <LabelField :label="$t('start_date')">
                            <DateTextfield
                                :value="schedule?.start_date"
                                :placeholder="`${$t('select_date')}`"
                                :rules="[formRules['required']]"
                                :hide-details="true"
                                @input="onInput('start_date', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField :label="$t('start')">
                            <TimeTextfield
                                :value="schedule?.event_time_start"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('event_time_start', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="6">
                        <LabelField :label="$t('end_date')">
                            <DateTextfield
                                :value="schedule.end_date"
                                :placeholder="String($t('select_date'))"
                                :hide-details="false"
                                :disabled="schedule.start_date ? !schedule.start_date.isValid() : true"
                                :value-from="
                                    schedule.start_date
                                        ? schedule.start_date.add(1, 'day').format('YYYY-MM-DD')
                                        : undefined
                                "
                                @input="onInput('end_date', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField :label="$t('end')">
                            <TimeTextfield
                                :value="schedule?.event_time_end"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('event_time_end', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="6">
                        <LabelField :label="$t('opening_time_start')">
                            <TimeTextfield
                                :value="schedule?.opening_time_start"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('opening_time_start', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField :label="$t('opening_time_end')">
                            <TimeTextfield
                                :value="schedule?.opening_time_end"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('opening_time_end', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField
                            :label="$t('interessen_tags_erweiterte_suche')"
                            :label-tip="$t('interessen_tags_erweiterte_suche_tip')"
                        >
                            <!-- we choose item-value="tag" so in the list later the tags are shown as readable text instead of id's -->
                            <TagSelect
                                :value="schedule.tags"
                                item-value="tag"
                                for-schedule
                                :placeholder="$t('placeholder_event_description_tags')"
                                :show-add="false"
                                :show-download="false"
                                clearable
                                hide-details="auto"
                                @input="onInput('tags', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="`${$t('zusatzinformationen')} / ${$t('alternativtext_fuer_printexport')}`">
                            <VTextField
                                :value="schedule?.description"
                                hide-details="auto"
                                dense
                                outlined
                                clearable
                                @input="onInput('description', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="3">
                        <ActionBtn @click="editRecord(schedule, false)">
                            <KpIcon icon-cls="calendar" color="white" />
                            {{ $t('recurring_type_single') }}
                        </ActionBtn>
                    </v-col>
                    <v-col cols="3">
                        <ActionBtn @click="editRecord(schedule, true)">
                            <KpIcon icon-cls="events" color="white" />
                            {{ $t('recurring_type_recurring') }}
                        </ActionBtn>
                    </v-col>
                    <v-col cols="3"> </v-col>
                    <v-col cols="3">
                        <ActionBtn color="accent" :disabled="!valid" @click="debounce(saveSchedule(schedule, false))">
                            <KpIcon icon-cls="save" color="white" />
                            {{ $t('save') }}
                        </ActionBtn>
                    </v-col>
                </v-row>
            </v-container>
        </v-form>

        <h4 class="mb-2">{{ $t('erfasste_termine') }}</h4>
        <BaseList
            class="mb-5"
            dense
            session-key="compact-schedule-list"
            :loading="loading"
            :loading-text="$t('loading')"
            :items="collection?.models ?? []"
            :headers="headers"
            hide-default-footer
            hide-top-toolbar
            disable-pagination
            :hide-toolbar="true"
            :query-field="false"
            @edit-item="editRecord"
            @delete-item="deleteRecord"
            @reload="reload"
        >
            <template #item.icon="{ item }">
                <KpIcon
                    v-if="item.isRecurring"
                    icon-cls="rotate"
                    color="grey"
                    :tooltip="$t('recurring_type_recurring')"
                />
                <KpIcon v-else icon-cls="calendar" color="grey" :tooltip="$t('recurring_type_single')" />
            </template>
            <template #item.start_date="{ item }">
                <div
                    :class="{
                        'text-right': true,
                        'text-decoration-line-through': item.schedule_status_ident === 'cancelled',
                    }"
                >
                    {{ item.start_date?.isValid() ? item.start_date.formatIf('DD.MM.YYYY') : '' }}
                </div>
                <div
                    v-if="item.end_date?.isValid() && item.end_date.isSame(item.start_date) !== true"
                    :class="{
                        'text-right': true,
                    }"
                >
                    -
                    <span
                        :class="{
                            'text-decoration-line-through': item.schedule_status_ident === 'cancelled',
                        }"
                    >
                        {{ item.end_date.formatIf('DD.MM.YYYY') }}</span
                    >
                </div>
            </template>
            <template #item.event_time_start="{ item }">
                <span>{{ item.timeString(item.event_time_start) }}</span>
            </template>
            <template #item.opening_time_start="{ item }">
                <span>{{ item.timeString(item.opening_time_start) }}</span>
            </template>
            <template #item.infos="{ item }">
                <div>{{ item.tags?.join(', ') }}</div>
                <div v-if="item?.description">{{ item?.description }}</div>
            </template>
        </BaseList>
        <ScheduleEditDialog
            v-if="showEdit"
            :record="schedule"
            :event="event"
            :value="showEdit"
            @input="showEdit = $event"
            @saved="reload"
            @cancelled="resetSchedule"
        />
    </div>
</template>
